import { ArrowRight24Icon, Clear24Icon, Close16Icon } from "@kaltura/ds-react-icons";
import { baseUrl, getDuration, getEntryUrl, translate } from "@kaltura/mediaspace-shared-utils";
import {
    CloseButtonMobile,
    CloseButtonWrapper,
    FullWidthIconButton,
    MediaName,
    StyledCloseButton,
    StyledSidebar,
    StyledSidebarContainer,
    StyledSidebarInnerContainer,
    StyledSidebarMedia,
} from "./styled-components";
import { SimpleThumbnail, TruncateWithTooltip } from "@kaltura/mediaspace-shared-ui";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import DetailsContainer from "./details/DetailsContainer";
import { SearchResultsContainer } from "./search-results/SearchResultsContainer";
import { BaseEntryResultType, ButtonClickAnalyticsType, FilterValues } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import PlayerContainer from "./player/PlayerContainer";
import { KalturaEntryStatus } from "kaltura-typescript-client/api/types/KalturaEntryStatus";
import { ReactNode, useEffect, useRef } from "react";

export interface ExploreProps {
    baseClassName?: string;

    /**
     * close button handler
     */
    onClose: () => void;

    /**
     * info to show
     */
    searchResult: BaseEntryResultType;

    /**
     * current search parameters, used for getting additional reasons
     */
    filterValues: FilterValues;

    /**
     * if true, player will show when possible. in that case, mediaContext must be passed to generate player KS.
     * if false, only thumbnail will be shown
     */
    showPlayer?: boolean;

    /**
     * the context in which media is playing, used when getting player ks
     */
    mediaContext?: string;

    /**
     * call to action component to show under the media.
     * if not passed, the default "Go to media" button will be shown
     */
    callToActionComponent?: ReactNode;

    /**
     * category id in case the bar is open in the context of a category
     */
    categoryId?: number;

    /**
     * show only the base info, even when displaying search results
     */
    showBaseInfoOnly?: boolean;

    /**
     * optional URL to fetch metadata for details container
     */
    metaDataFetchUrl?: string;
}

/**
 * shows detailed info about selected search result
 */
export function ExploreSideBarContent({
    onClose,
    searchResult,
    filterValues,
    baseClassName: baseClassNameProp,
    showPlayer: showPlayerProp = false,
    mediaContext,
    callToActionComponent,
    categoryId,
    showBaseInfoOnly = false,
    metaDataFetchUrl,
}: ExploreProps) {
    const baseClassName = `${baseClassNameProp}-explore-sidebar`;
    const config = useConfig();
    const enableEntryTitles = config?.application?.enableEntryTitles;
    const entryNameForUrl = enableEntryTitles ? searchResult.object.name : undefined;

    const goToMedia = (entryId: string, entryName?: string) => {
        document.location.href = getEntryUrl(entryId, undefined, entryName);
    };

    const sendButtonAnalytics = useButtonAnalytics();
    const handleClose = () => {
        sendButtonAnalytics("Explore sidebar - Close sidebar", ButtonClickAnalyticsType.CLOSE);
        onClose();
    };

    const goToMediaClick = () => {
        goToMedia(searchResult.object.id, entryNameForUrl);
        sendButtonAnalytics("Explore sidebar - Go to Media", ButtonClickAnalyticsType.LINK);
    };

    const showPlayer =
        showPlayerProp
        // hide the player for entries that are still transcoding
        && [KalturaEntryStatus.ready, KalturaEntryStatus.noContent].includes(searchResult.object.status)
        // show the player only for entries that have a duration (VOD, webcasts with a recording, audio, YouTube...)
        && !!getDuration(searchResult.object);

    const sideBarRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        // focus the sidebar when it opens
        sideBarRef.current?.focus();
    }, []);

    return (
        <StyledSidebar className={`${baseClassName}`} ref={sideBarRef} tabIndex={-1}>
            <CloseButtonWrapper className={`${baseClassName}-close-button-wrapper`}>
                <StyledCloseButton
                    startIcon={<Clear24Icon />}
                    variant={"borderless"}
                    color={"secondary"}
                    onClick={handleClose}
                    className={`${baseClassName}-close-button`}
                    aria-label={translate("Close %1 Explore", [searchResult.object.name])}
                >
                    {translate("Close")}
                </StyledCloseButton>
            </CloseButtonWrapper>
            <StyledSidebarMedia className={`${baseClassName}-thumbnail`}>
                {showPlayer && (
                    <PlayerContainer
                        url={baseUrl + "/entry/get-player-ks-for-entry/context/" + mediaContext}
                        entry={searchResult.object}
                    />
                )}
                {!showPlayer && (
                    <SimpleThumbnail
                        thumbnailUrl={`${searchResult.object.thumbnailUrl}/width/552/height/311/type/3`}
                        name={searchResult.object.name}
                        roundCorners={false}
                    />
                )}
            </StyledSidebarMedia>
            <CloseButtonMobile
                color={"translucent"}
                variant={"pill"}
                onClick={handleClose}
                aria-label={translate("Close")}
            >
                <Close16Icon />
            </CloseButtonMobile>
            {callToActionComponent && callToActionComponent}
            {!callToActionComponent && (
                <FullWidthIconButton
                    startIcon={<ArrowRight24Icon />}
                    variant={"pill"}
                    onClick={goToMediaClick}
                    className={`${baseClassName}-go-to-media-button`}
                >
                    {translate("Go to media")}
                </FullWidthIconButton>
            )}
            <StyledSidebarContainer className={`${baseClassName}-explore-details-container`}>
                <StyledSidebarInnerContainer detailsClassName={`${baseClassName}-explore-details`}>
                    <TruncateWithTooltip lines={3} text={searchResult.object.name}>
                        <MediaName component={"h2"} variant={"h4"} className={`${baseClassName}-explore-details-name`}>
                            {searchResult.object.name}
                        </MediaName>
                    </TruncateWithTooltip>
                    <DetailsContainer
                        searchResult={searchResult}
                        className={`${baseClassName}-explore-details`}
                        categoryId={categoryId}
                        metaDataFetchUrl={metaDataFetchUrl}
                    />
                    <SearchResultsContainer
                        className={`${baseClassName}-explore-reasons`}
                        entryId={searchResult.object.id}
                        searchResult={searchResult}
                        filterValues={filterValues}
                        showBaseInfoOnly={showBaseInfoOnly}
                    />
                </StyledSidebarInnerContainer>
            </StyledSidebarContainer>
        </StyledSidebar>
    );
}

export default ExploreSideBarContent;
